import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Badge,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
} from '@/components/ui';
import { CompanyLogo } from '@/features/internships/CompanyLogo';
import { VERIFICATION_TONES, humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * The company register.
 *
 * Deliberately not the verification queue: that screen only ever holds what is
 * waiting for a decision, and empties the moment one is made. An admin asked
 * "who is Acme and are they actually using us?" has nowhere to go from there,
 * which is what this answers.
 */
const CLOSED = 'CLOSED';

/** The verification filter, as tabs. Same values the dropdown sent. */
const VERIFICATION_TABS = [
  { key: '', label: 'All' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'VERIFIED', label: 'Verified' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'UNVERIFIED', label: 'Not submitted' },
  // Not a verification state: companies their owners closed, which every other
  // tab leaves out. Opened from here, one can be restored.
  { key: CLOSED, label: 'Closed' },
];

const PAGE_SIZE = 20;

export function AdminCompaniesPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [featured, setFeatured] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(status === CLOSED ? { closed: true } : status ? { statuses: status } : {}),
    ...(featured ? { is_featured: featured === 'yes' } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-companies', params],
    queryFn: () => adminApi.listCompanies(params),
    // Without this every keystroke unmounts the page for the loading state,
    // taking the search box and the focus in it -- so half of what was typed
    // silently becomes the filter.
    placeholderData: keepPreviousData,
  });

  /** Every filter change is a new result set, so page 1 is the only sane page. */
  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  const activeCount = [search !== '', status !== '', featured !== ''].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setFeatured('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Companies"
        description="Every company on the platform, whatever state its verification is in."
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Verification"
            tabs={VERIFICATION_TABS}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Featured"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={featured}
              onChange={change(setFeatured)}
            >
              <option value="">All companies</option>
              <option value="yes">Featured only</option>
              <option value="no">Not featured</option>
            </Select>
            <SearchInput
              label="Search companies"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Company, location or owner"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading companies…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="No companies"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No companies yet"
                description="Companies appear here as soon as they register."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              accent={VERIFICATION_TONES[row.verification_status] ?? 'neutral'}
              leading={<CompanyLogo company={row} className="size-11 rounded-xl" iconSize={20} />}
              title={row.name}
              to={paths.admin.companyDetail(row.id)}
              badges={
                <>
                  <Badge dot tone={VERIFICATION_TONES[row.verification_status] ?? 'neutral'}>
                    {humanizeStatus(row.verification_status)}
                  </Badge>
                  {row.closed_at && <Badge tone="danger">Closed</Badge>}
                  {row.is_featured && <Badge tone="brand">Featured</Badge>}
                </>
              }
              subtitle={
                [row.industry, row.location].filter(Boolean).join(' · ') ||
                'No industry or location yet'
              }
              facts={[
                { icon: 'user', text: `Owner: ${row.owner_name ?? '—'}` },
                row.owner_email && { icon: 'send', text: row.owner_email },
                // Published is the number that matters; the total says how
                // much is sitting in drafts behind it.
                {
                  icon: 'briefcase',
                  text: `${row.published_internship_count} of ${row.internship_count} internships published`,
                },
                {
                  icon: 'application',
                  text: `${row.application_count} ${
                    row.application_count === 1 ? 'application' : 'applications'
                  }`,
                },
                {
                  icon: 'users',
                  text: `${row.member_count} ${row.member_count === 1 ? 'team member' : 'team members'}`,
                },
                { icon: 'calendar', text: `Joined ${formatDate(row.created_at)}` },
              ]}
              actions={
                <ViewButton to={paths.admin.companyDetail(row.id)} title={`Open ${row.name}`} />
              }
            />
          ))}
        </BoxList>
      </ListCard>
    </div>
  );
}
